import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * SCRUM-2260: the blog post form, written as a createSlice like every reducer
 * in this client (SCRUM-1954) and mounted LOCALLY with
 * `useReducer(postForm.reducer, ...)` — the unsaved draft of the form, not store
 * data. The SCRUM-2127 pattern. The create screen uses it first; the update
 * screen joins in SCRUM-2261 with `postLoaded` and `postSaved`.
 */
export type PostBlock = { title?: string; description?: string; image?: unknown; [key: string]: unknown };

/**
 * A byline user as the post read returns it: an id string, or a populated user.
 */
export const userIdOf = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  const id = (value as { _id?: unknown })._id;
  return id ? String(id) : null;
};

type PostFormState = {
  blocks: PostBlock[];
  /**
   * The write body the screen has built so far. Create screen: the processed blocks, then the
   * last submitted post. Update screen: the loaded post, then the last submitted post.
   */
  data: Record<string, unknown> | null;
  reactQuillText: string;
  picture: any; // any: a PickedImage whose context altOf reads as MediaImage; PickedImage types context unknown (ImageUploadContainer.tsx:13)
  title: string;
  /** Set by a submit, cleared when the server's message arrives; the block editor starts over while it is set. */
  reset: boolean;
  type: boolean;
  editor: string | null;
  reviewer: string | null;
};

const initialState: PostFormState = {
  blocks: [{ title: "", description: "", image: "" }],
  data: null,
  reactQuillText: "",
  picture: "",
  title: "",
  reset: false,
  type: false,
  editor: null,
  reviewer: null,
};

export const postForm = createSlice({
  name: "postForm",
  initialState,
  reducers: {
    titleChanged(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    typeChanged(state, action: PayloadAction<boolean>) {
      state.type = action.payload;
    },
    contentChanged(state, action: PayloadAction<string>) {
      state.reactQuillText = action.payload;
    },
    pictureReceived(state, action: PayloadAction<unknown>) {
      state.picture = action.payload;
    },
    editorChanged(state, action: PayloadAction<string | null>) {
      state.editor = action.payload;
    },
    reviewerChanged(state, action: PayloadAction<string | null>) {
      state.reviewer = action.payload;
    },
    // The editor added or removed a block row.
    blocksSet(state, action: PayloadAction<PostBlock[]>) {
      state.blocks = action.payload;
    },
    // Blocks after their images were handled: fed back to the editor and kept in the write body.
    blocksProcessed(state, action: PayloadAction<PostBlock[]>) {
      state.blocks = action.payload;
      state.data = { ...state.data, blocks: action.payload };
    },
    // Add was pressed: the body is kept as built. The fields stay until the post is sent.
    submitted(state, action: PayloadAction<Record<string, unknown>>) {
      state.data = action.payload;
    },
    // The post was sent: the text fields and the byline start over. Not dispatched when the
    // cover-image upload fails, so the editor keeps their input (SCRUM-2281).
    postSent(state) {
      state.title = "";
      state.reactQuillText = "";
      state.editor = null;
      state.reviewer = null;
      state.reset = true;
    },
    // The server answered: the block editor may start over again next time.
    resetCleared(state) {
      state.reset = false;
    },
    // Update screen: the post read arrived (also another post, without an unmount), so every
    // field starts over from it.
    postLoaded(state, action: PayloadAction<Record<string, any>>) { // any: blog.post_byid is typed any (blogSlice.ts:33)
      const post = action.payload;
      state.data = post;
      state.type = !!post.type;
      state.title = post.title || "";
      state.reactQuillText = (post.content || "").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
      state.blocks = Array.isArray(post.blocks) ? post.blocks : [];
      state.picture = post.picture || "";
      state.editor = userIdOf(post.editor);
      state.reviewer = userIdOf(post.reviewer);
    },
    // Update screen: Update was pressed; the next save spreads this body. The fields stay.
    postSaved(state, action: PayloadAction<Record<string, unknown>>) {
      state.data = action.payload;
    },
  },
});
